<template>
	<main-page :user="user" color="orange">
		<template v-slot:title> PROFILE </template>

		<div class="container">
			<div v-if="assignment" class="row content">
				<div class="d-flex flex-row align-items-top w-100 mt-3">
					<div
						class="shadow-sm mr-3 d-flex flex-column justify-content-center align-items-center si-font si-blue text-white"
						style="width: 50%"
					>
						<div :class="{ [hideInstructions ? 'text-lg' : 'text-xl']: true }">{{ assignment.name }}</div>
					</div>
					<div class="card shadow-sm ml-3" style="width: 50%">
						<div
							class="px-3 py-1 text-lg si-font si-blue text-white text-left _600 clickable d-flex flex-row align-items-center"
							@click="toggleInstructions"
						>
							<div class="flex-grow">Instructions</div>
							<i class="fas fa-caret-down anim-rotate" :class="{ 'fa-rotate-90': hideInstructions }" />
						</div>
						<div
							v-if="!hideInstructions"
							class="card-body p-3"
							style="text-align: left"
							v-html="program.profile_config.instructions"
						></div>
					</div>
				</div>
				<transition name="slide-fade">
					<div v-if="question" class="card shadow-sm p-4 w-100 text-left mt-4">
						<h3 :ref="`question-${question.sequence + 1}`" class="mt-0 mb-4">
							{{ question.sequence + 1 }}. {{ question.prompt }}
						</h3>
						<profile-question
							class="w-100"
							:color="getColor(question.sequence)"
							:groupName="question.sequence"
							v-model="question.result"
						/>
						<div class="d-flex flex-row justify-content-end align-items-center mt-3">
							<button
								v-if="seq != 0"
								class="btn btn-md btn-no-border btn-si-yellow d-flex flex-row align-items-center mr-3"
								@click="prev"
							>
								<i class="fas fa-caret-left mr-2" /> Back
							</button>
							<button
								v-if="seq < profileQuestions.length - 1"
								class="btn btn-md btn-no-border btn-si-green d-flex flex-row align-items-center ml-auto"
								@click="next"
							>
								Next <i class="fas fa-caret-right ml-2" />
							</button>
							<button
								v-if="seq == profileQuestions.length - 1"
								class="btn btn-lg btn-no-border btn-si-green d-flex flex-row align-items-center ml-auto"
								@click="submit"
								:disabled="submitting"
							>
								Submit
							</button>
						</div>
					</div>
				</transition>
			</div>
		</div>

		<template v-slot:footer>
			<div class="col-9 row no-gutters">
				<div class="col-8 d-flex" style="flex-wrap: wrap;">
					<a v-for="(question, i) in profileQuestions" :key="i" class="progress-num mr-2" @click="goTo(i)">
						<div class="progress-num-top" :class="{ active: seq == i }">
							{{ i + 1 }}
						</div>
						<div>
							<i v-if="question.result.score == null" class="text-very-muted fas fa-circle-notch" />
							<i v-if="question.result.score != null" class="fas fa-check" />
						</div>
					</a>
				</div>
				<div class="col-4 text-right">
					<a class="btn btn-si-secondary mr-3" href="#/profile_entry"> Cancel </a>
					<a class="btn btn-si-secondary mr-3" v-if="!submitted" @click="saveForLater"> Save For Later </a>
					<a class="btn btn-si-secondary mr-3" v-if="submitted" @click="submit"> Submit Changes </a>
				</div>
			</div>
		</template>

		<b-modal id="cantSubmitModal" headerClass="indigo">
			<template v-slot:modal-title>
				<h3 class="modal-title full-bar-title">Cannot Submit Profile</h3>
			</template>
			<div class="p-2">
				<div v-for="(error, i) in submitErrors" :key="i">
					<div v-if="error.score && error.reasoning">
						<strong :class="{ [`si-text-${getColor(i)}`]: true }">Question {{ error.seq + 1 }}</strong>
						requires a rating and explanation
					</div>
					<div v-if="error.score && !error.reasoning">
						<strong :class="{ [`si-text-${getColor(i)}`]: true }">Question {{ error.seq + 1 }}</strong>
						requires a rating
					</div>
					<div v-if="error.reasoning && !error.score">
						<strong :class="{ [`si-text-${getColor(i)}`]: true }">Question {{ error.seq + 1 }}</strong>
						requires an explanation
					</div>
				</div>
			</div>
			<template v-slot:modal-footer="{ _, cancel }">
				<button type="button" class="btn btn-primary" @click="cancel">Okay</button>
			</template>
		</b-modal>
	</main-page>
</template>

<script>
import MainPage from "@/components/MainPage";
import ProfileQuestion from "@/components/ProfileQuestion";

import ProfileService from "@/services/ProfileService";
import ProgramService from "@/services/ProgramService";
import SectionService from "@/services/SectionService";
import Notie from "@/services/NotieService";
import fs from "@/services/FormatService";
import BB from "bluebird";
import _ from "lodash";

export default {
	name: "Profile",

	props: ["user"],

	components: { MainPage, ProfileQuestion },

	data() {
		return {
			fs: fs,

			hideInstructions: null,
			profileQuestions: null,
			program: null,
			context: null,
			assignment: null,
			seq: 0,
			submitErrors: [],
			savedProfile: null,
			submitting: false,
			submitted: false,
		};
	},

	created() {
		this.assignmentID = this.$route.params.assignmentID;

		BB.props({
			program: ProgramService.getContextProgram(),
			context: SectionService.getContextPrefilled(),
			saved: ProfileService.getProfileInProgress(this.assignmentID),
			submitted: ProfileService.getUserProfile(this.assignmentID),
		})
			.then((r) => {
				console.log("CONTEXT PROGRAM", r);
				this.program = r.program.data;
				this.context = r.context.data;
				this.assignment = _.find(this.context.profiles, { id: this.assignmentID });
				if (r.saved.data && r.saved.data.responses && r.saved.data.responses.length > 0) {
					this.savedProfile = r.saved.data;
				} else if (r.submitted.data && r.submitted.data.responses && r.submitted.data.responses.length > 0) {
					this.savedProfile = r.submitted.data;
					this.submitted = true;
				}

				this.profileQuestions = [];
				_.each(this.program.profile_config.questions, (q, i) => {
					let result = {};
					if (this.savedProfile) {
						result = _.find(this.savedProfile.responses, { item_id: q.id });
					}
					this.profileQuestions.push({
						item_id: q.id,
						prompt: q.text,
						result: result,
						sequence: i,
						required_score: q.required_score,
						required_reasoning: q.required_reasoning,
					});
				});
			})
			.catch((e) => {
				console.log(e);
				Notie.error("Failed to load program", e);
			});
	},

	computed: {
		question() {
			if (this.seq == null) null;
			if (!this.profileQuestions) return null;
			if (this.profileQuestions.length == 0) return null;
			if (this.seq < 0) return null;
			if (this.seq >= this.profileQuestions.length) return null;

			return this.profileQuestions[this.seq];
		},
	},

	methods: {
		submit() {
			if (this.submitting) {
				return;
			}
			this.submitting = true
			this.validateSubmit();
			if (this.submitErrors && this.submitErrors.length > 0) {
				this.$bvModal.show("cantSubmitModal");
				this.submitting = false;
				return;
			}

			let profile = this.getSubmittableProfile();

			ProfileService.saveProfile(profile)
				.then(() => {
					Notie.success("Profile submitted");
					this.$router.push("/profile_entry");
				})
				.catch((e) => {
					console.log(e);
					Notie.error("Failed to submit profile", e);
					this.submitting = false;
				});
		},

		getSubmittableProfile() {
			let responses = [];
			_.each(this.profileQuestions, (pq) => {
				let result = pq.result;
				result.item_id = pq.item_id;
				responses.push(result);
			});

			let profile = {
				responses: responses,
			};

			profile.section_id = this.user.context.id;
			profile.assignment_id = this.assignmentID;

			// Using the same ID as the saved profile allows the saved profile to be deleted on submit
			if (this.savedProfile) {
				profile.id = this.savedProfile.id;
			}

			return profile;
		},

		next() {
			if (this.hideInstructions == null) {
				this.hideInstructions = true;
			}
			let current = this.seq;
			this.seq = null;
			this.$nextTick(() => {
				this.seq = current + 1;
			});
		},

		prev() {
			if (this.hideInstructions == null) {
				this.hideInstructions = true;
			}
			let current = this.seq;
			this.seq = null;
			this.$nextTick(() => {
				this.seq = current - 1;
			});
		},

		goTo(i) {
			if (this.hideInstructions == null) {
				this.hideInstructions = true;
			}
			this.seq = null;
			this.$nextTick(() => {
				this.seq = i;
			});
		},

		getColor(i) {
			switch (i % 4) {
				case 0:
					return "blue";
				case 1:
					return "green";
				case 2:
					return "yellow";
				case 3:
					return "orange";
			}
		},

		validateSubmit() {
			this.submitErrors = [];
			_.each(this.profileQuestions, (pq, i) => {
				let any = false;
				let error = {
					seq: i,
				};
				if (pq.required_score && (pq.result.score == null || pq.result.score == undefined)) {
					any = true;
					error.score = true;
				}
				if (pq.required_reasoning && !pq.result.reasoning) {
					any = true;
					error.reasoning = true;
				}
				if (any) {
					this.submitErrors.push(error);
				}
			});
		},

		saveForLater() {
			let profile = this.getSubmittableProfile();

			ProfileService.saveProfileInProgress(profile)
				.then(() => {
					Notie.success("Profile saved");
					this.$router.push("/profile_entry");
				})
				.catch((e) => {
					console.log(e);
					Notie.error("Failed to save profile", e);
				});
		},

		toggleInstructions() {
			this.hideInstructions = !this.hideInstructions;
		},
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.splash-buttons {
	margin-top: 3rem;
	margin-bottom: 3rem;
}
.splash-button-zone {
	padding: 3rem;
}
.splash-button-link > img {
	transition: transform 0.15s ease-in-out, opacity 0.15s ease-in-out;
}
.splash-button-link:hover > img {
	transform: scale(1.05);
}
.splash-button {
	width: 100%;
}

.progress-num {
	width: 25px;
	font-size: 18px;
	font-weight: 600;
	transition: transform 0.15s ease-in-out;
}
.progress-num-top {
	border: 2px solid transparent;
	line-height: 1;
	margin-bottom: 2px;
	padding-left: 2px;
	padding-right: 3px;
	margin-left: -2px;
	margin-right: -2px;
}
.progress-num-top.active {
	border-color: black;
	border-radius: 5px;
}

.progress-num:hover {
	transform: scale(1.15);
	opacity: 0.5;
}
.slide-fade-enter-active {
	transition: all 0.3s ease;
}
.slide-fade-leave-active {
	transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
	transform: translateX(10px);
	opacity: 0;
}

.text-xl {
	font-size: 48px;
}
</style>
